import { useEffect, useMemo } from 'react';
import { Book1, Teacher, TickCircle } from 'iconsax-reactjs';
import { cn } from '../../../../lib/utils';
import { GRADE_OPTIONS, LESSONS_DATABASE } from '../../../../constants/education';
import { getGoldenChallengeSuggestions } from '../../../../services/webServices/challenge/suggestions';
import type { GoldenChallengeOption } from '../../../../services/webServices/challenge/types';
import WizardSelect from './WizardSelect';
import WizardStepHeader from './WizardStepHeader';
import type { ChainFormState } from './wizardTypes';
import ChallengeAction from '../ChallengeAction';

const ALL_GRADES = [...GRADE_OPTIONS.elementary, ...GRADE_OPTIONS.middle, ...GRADE_OPTIONS.high];

const LESSON_OPTIONS_BY_GRADE: Record<string, { value: string; label: string }[]> = Object.values(LESSONS_DATABASE).reduce<Record<string, { value: string; label: string }[]>>((acc, gradesForStage) => {
  Object.entries(gradesForStage).forEach(([grade, lessons]) => {
    const baseGrade = grade.split('_')[0];
    const merged = [...(acc[baseGrade] ?? [])];
    lessons.forEach((lesson) => {
      if (!merged.some((item) => item.value === lesson.value)) {
        merged.push(lesson);
      }
    });
    acc[baseGrade] = merged;
  });
  return acc;
}, {});

const getOptionText = (option: GoldenChallengeOption): string => option.content ?? option.text ?? '';

type Props = {
  state: ChainFormState;
  disabled: boolean;
  lockCompletedDays?: boolean;
  onChange: (updater: (state: ChainFormState) => ChainFormState) => void;
};

export default function StepDayQuestions({ state, disabled, lockCompletedDays = false, onChange }: Props) {
  const lessonOptions = LESSON_OPTIONS_BY_GRADE[state.grade] ?? [];
  const activeDay = state.days.find((day) => day.weekDay === state.activeWeekDay) ?? state.days[0];

  const visibleSuggestions = useMemo(() => activeDay.suggestions.slice(activeDay.suggestionOffset, activeDay.suggestionOffset + 2), [activeDay.suggestions, activeDay.suggestionOffset]);

  const canFetchSuggestions = Boolean(state.lesson && state.grade);
  const poolLoaded = state.days.some((day) => day.suggestions.length > 1);
  const isActiveDayLocked = lockCompletedDays && Boolean(activeDay.selectedSuggestionId);

  useEffect(() => {
    if (!canFetchSuggestions) return;
    if (poolLoaded || activeDay.suggestionsLoading) return;

    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.lesson, state.grade]);

  const setActiveDay = (day: ChainFormState['days'][number]) => {
    onChange((prev) => ({ ...prev, activeWeekDay: day.weekDay }));
  };

  const setLesson = (lesson: string) =>
    onChange((prev) => ({
      ...prev,
      lesson,
      days: prev.days.map((day) => ({
        ...day,
        suggestions: [],
        suggestionOffset: 0,
        selectedSuggestionId: null,
      })),
    }));

  const setGrade = (grade: string) =>
    onChange((prev) => ({
      ...prev,
      grade,
      lesson: '',
      days: prev.days.map((day) => ({
        ...day,
        suggestions: [],
        suggestionOffset: 0,
        selectedSuggestionId: null,
      })),
    }));

  const setDaySuggestions = (weekDay: number, updater: (day: ChainFormState['days'][number]) => ChainFormState['days'][number]) => {
    onChange((prev) => ({
      ...prev,
      days: prev.days.map((day) => (day.weekDay === weekDay ? updater(day) : day)),
    }));
  };

  const loadSuggestions = async () => {
    onChange((prev) => ({
      ...prev,
      days: prev.days.map((day) => ({ ...day, suggestionsLoading: true })),
    }));

    const pool = await getGoldenChallengeSuggestions({
      grade: Number(state.grade),
      // TODO: shitty code, must be fixed
      lesson: state.lesson.split('_')[0],
    });

    onChange((prev) => ({
      ...prev,
      days: prev.days.map((day) => ({
        ...day,
        suggestions: [...day.suggestions, ...pool.filter((suggestion) => !day.suggestions.some((existing) => existing.id === suggestion.id))],
        suggestionOffset: 0,
        suggestionsLoading: false,
      })),
    }));
  };

  const showNextSuggestions = () => {
    setDaySuggestions(activeDay.weekDay, (day) => {
      const nextOffset = day.suggestionOffset + 2;
      return {
        ...day,
        suggestionOffset: nextOffset < day.suggestions.length ? nextOffset : 0,
      };
    });
  };

  const hasMoreSuggestions = activeDay.suggestions.length > 2;

  const selectSuggestion = (suggestionId: string) => {
    setDaySuggestions(activeDay.weekDay, (day) => ({
      ...day,
      selectedSuggestionId: suggestionId,
    }));
  };

  const handleSuggestionDeleted = (suggestionId: string) => {
    setDaySuggestions(activeDay.weekDay, (day) => ({
      ...day,
      suggestions: day.suggestions.filter((suggestion) => suggestion.id !== suggestionId),
      selectedSuggestionId: day.selectedSuggestionId === suggestionId ? null : day.selectedSuggestionId,
    }));
  };

  return (
    <div className="flex flex-col gap-6" dir="rtl">
      <WizardStepHeader title="افزودن زنجیره جدید" />

      <div className="grid gap-3 grid-cols-2 max-w-[530px]">
        <WizardSelect value={state.grade} placeholder="پایه" icon={<Teacher size={25} variant="Outline" />} options={ALL_GRADES} disabled={disabled} onChange={setGrade} />
        <WizardSelect value={state.lesson} placeholder="رشته" icon={<Book1 size={25} variant="Outline" />} options={lessonOptions} disabled={disabled || !state.grade} onChange={setLesson} />
      </div>

      <div className="flex flex-wrap gap-2">
        {state.days.map((day) => {
          const isActive = day.weekDay === activeDay.weekDay;
          const isLocked = lockCompletedDays && Boolean(day.selectedSuggestionId);

          return (
            <button
              key={day.weekDay}
              type="button"
              disabled={disabled || isLocked}
              onClick={() => setActiveDay(day)}
              className={cn(
                'h-8 rounded-lg border px-4 text-sm font-medium transition-colors',
                disabled && 'opacity-50',
                isLocked && 'cursor-not-allowed border-primaryVar-200 bg-primaryVar-100 text-primaryVar-600',
                !isLocked && isActive && day.isGolden && 'border-warningVar-500 bg-warningVar-500 text-white',
                !isLocked && isActive && !day.isGolden && 'border-primaryVar-300 bg-primaryVar-500 text-white',
                !isLocked && !isActive && day.isGolden && 'border-dashed border-warningVar-500 text-warningVar-600',
                !isLocked && !isActive && !day.isGolden && 'border-gray-300 text-gray-400 hover:border-primaryVar-300'
              )}
            >
              {day.label}
            </button>
          );
        })}
      </div>

      {!canFetchSuggestions ? (
        <p className="text-sm text-gray-400">برای مشاهده سوالات پیشنهادی، ابتدا پایه و رشته را انتخاب کنید.</p>
      ) : (
        <>
          <p className="text-sm text-gray-500">یکی از دو سؤال زیر را انتخاب کنید یا با «سوالات بعدی»، پیشنهادهای جدید ببینید.</p>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {visibleSuggestions.map((suggestion) => {
              const isSelected = suggestion.id === activeDay.selectedSuggestionId;

              return (
                <div key={suggestion.id} className={cn('flex flex-col rounded-[20px] p-2', isSelected ? 'bg-primaryVar-100' : 'bg-gray-200')}>
                  <div className="rounded-xl bg-white px-3 pb-3 pt-3">
                    <p className="mb-4 text-center text-[13px] font-medium text-gray-800">{suggestion.content}</p>

                    <div className="flex flex-col gap-3">
                      {suggestion.options.map((option, index) => {
                        const isCorrect = option.is_correct === true;

                        return (
                          <div key={index} className={cn('flex h-11 items-center justify-between gap-2 rounded-xl border px-4 text-sm', isCorrect ? 'border-primaryVar-200 bg-primaryVar-50 text-primaryVar-700' : 'border-gray-200 text-gray-600')}>
                            <span className={cn('size-4 shrink-0 rounded-full border', isCorrect ? 'border-[5px] border-primaryVar-300 bg-white' : 'border-gray-300')} />

                            <span className="flex-1 truncate text-right text-xs font-medium">{getOptionText(option)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center justify-between px-[12px]">
                    <button type="button" disabled={disabled || isActiveDayLocked} onClick={() => selectSuggestion(suggestion.id)} className="flex items-center gap-x-2 px-2 pb-1 pt-3 text-[13px] font-medium text-gray-600 disabled:opacity-50">
                      <TickCircle size={23} variant={isSelected ? 'Bold' : 'Outline'} color={isSelected ? '#245cf7' : '#d1d5db'} />
                      انتخاب این سوال
                    </button>

                    {!isSelected && <ChallengeAction id={suggestion.id} onActionSuccessed={() => handleSuggestionDeleted(suggestion.id)} />}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            disabled={disabled || isActiveDayLocked || activeDay.suggestionsLoading || !hasMoreSuggestions}
            onClick={showNextSuggestions}
            className="self-center rounded-lg border border-primaryVar-500 px-5 h-10 text-sm font-medium text-primaryVar-500 hover:bg-primaryVar-50 disabled:opacity-50"
          >
            {activeDay.suggestionsLoading ? 'در حال بارگذاری...' : 'سوالات بعدی'}
          </button>
        </>
      )}
    </div>
  );
}
